import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { PlusCircle, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import DateRangeFilter, { useDateRangeFilter } from "../../components/list/DateRangeFilter";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { makeGrandTotalSummary } from "../../components/list/GrandTotalSummary";
import { sumField } from "../../components/list/listExportUtils";
import { useListStockOutsQuery } from "../../stockApi";
import { useCanCreateStockOut } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const ISSUED_TO_OPTIONS = [
  { value: "", label: "All" },
  { value: "EMPLOYEE", label: "Employee" },
  { value: "CUSTOMER", label: "Customer" },
];

const StockOutList = () => {
  const canCreate = useCanCreateStockOut();
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [issuedToType, setIssuedToType] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [issuedToName, setIssuedToName] = useState("");
  const { dateFrom, setDateFrom, dateTo, setDateTo } = useDateRangeFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading } = useListStockOutsQuery({
    branchId: selectedBranchId ?? undefined,
    issuedToType: issuedToType || undefined,
    search: search || undefined,
    includeDeleted,
    pageSize: 100,
  });

  // issuedToEmployeeName/issuedToCustomerName are the only "who" fields the
  // list endpoint returns (no employee code on this record) - filtering by
  // name here rather than code.
  const filteredItems = (data?.items || []).filter((r) => {
    if (r.stockOutDate) {
      const d = dayjs(r.stockOutDate).format("YYYY-MM-DD");
      if ((dateFrom && d < dateFrom) || (dateTo && d > dateTo)) return false;
    }
    if (issuedToName.trim()) {
      const name = (r.issuedToEmployeeName || r.issuedToCustomerName || "").toLowerCase();
      if (!name.includes(issuedToName.trim().toLowerCase())) return false;
    }
    return true;
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const exportColumns = [
    { header: "Stock-Out No", value: (r) => r.stockOutNo },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Issued To", value: (r) => (r.issuedToType === "EMPLOYEE" ? `Employee: ${r.issuedToEmployeeName || "-"}` : `Customer: ${r.issuedToCustomerName || "-"}`) },
    { header: "Purpose", value: (r) => r.issuedForPurpose || "-" },
    { header: "Date", value: (r) => (r.stockOutDate ? new Date(r.stockOutDate).toLocaleDateString() : "-") },
    // VAT columns (2026-09-14): Amount is ex VAT (the stored TotalAmount), VAT and
    // Total Inc VAT come from Batch172.
    { header: "Amount (ex VAT)", value: (r) => Number(r.totalAmount || 0).toFixed(2), align: "right" },
    { header: "VAT", value: (r) => Number(r.vatAmount || 0).toFixed(2), align: "right" },
    { header: "Total Inc VAT", value: (r) => Number(r.grandTotal || 0).toFixed(2), align: "right" },
    { header: "Status", value: (r) => (r.isDeleted ? "Deleted" : "Issued") },
  ];
  const exportTotals = {
    "Amount (ex VAT)": sumField(filteredItems, "totalAmount").toFixed(2),
    VAT: sumField(filteredItems, "vatAmount").toFixed(2),
    "Total Inc VAT": sumField(filteredItems, "grandTotal").toFixed(2),
  };

  const columns = [
    {
      // Who it was issued to leads, Stock-Out number is the link underneath
      // (2026-09-12).
      title: "Issued To",
      dataIndex: "stockOutNo",
      render: (value, record) => (
        <PrimaryCell
          primary={record.issuedToType === "EMPLOYEE" ? record.issuedToEmployeeName : record.issuedToCustomerName}
          secondary={value}
          to={paths.stockOutDetail(record.recordGuid)}
          extra={record.issuedToType === "EMPLOYEE" ? "Employee" : "Customer"}
        />
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Purpose", dataIndex: "issuedForPurpose", render: (v) => v || "-" },
    { title: "Date", dataIndex: "stockOutDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Amount (ex VAT)", dataIndex: "totalAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "VAT", dataIndex: "vatAmount", render: (v) => Number(v || 0).toFixed(2) },
    { title: "Total Inc VAT", dataIndex: "grandTotal", render: (v) => <strong>{Number(v || 0).toFixed(2)}</strong> },
    {
      title: "Status",
      dataIndex: "isDeleted",
      render: (v) => (v ? <span className="badge badge-linedanger">Deleted</span> : <span className="badge badge-linesuccess">Issued</span>),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button type="button" className="btn btn-link p-2" title="View" onClick={() => navigate(paths.stockOutDetail(record.recordGuid))}>
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock-Out</h4>
              <h6>Stock issued to an employee or a customer</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockOutNew)}>
                <PlusCircle className="me-2" size={16} />
                New Stock-Out
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search Stock-Out No"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                <select className="form-control form-control-sm" style={{ width: 130 }} value={issuedToType} onChange={(e) => setIssuedToType(e.target.value)}>
                  {ISSUED_TO_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Issued to name"
                  style={{ width: 160 }}
                  value={issuedToName}
                  onChange={(e) => setIssuedToName(e.target.value)}
                />
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="stockout-include-deleted"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="stockout-include-deleted">Show deleted</label>
              </div>
              <ListExportToolbar
                title="Stock-Out"
                subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                branchCode={branchCode}
                columns={exportColumns}
                rows={filteredItems}
                totals={exportTotals}
                fileName="StockOut"
                groupBy={(r) => (r.issuedToType === "EMPLOYEE" ? r.issuedToEmployeeName : r.issuedToCustomerName) || "Unknown"}
              />
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredItems}
                rowKey="recordGuid"
                loading={isLoading}
                summary={makeGrandTotalSummary(columns, filteredItems, ["totalAmount", "vatAmount", "grandTotal"], "stockOutNo")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockOutList;
